import { DateTime, Duration } from 'luxon';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDateTime, getDurationSignificantUnit } from '@_/utils';

export interface FormattedDateTimeProps {
  dateStyle?: 'long' | 'short' | 'numeric';
  relative?: boolean;
  relativeStyle?: 'long' | 'short' | 'narrow';
  relativeMessage?: (value: React.ReactNode) => React.ReactNode;
  relativeThreshold?: Duration | null;
  showDate?: boolean;
  showYear?: boolean;
  showMonth?: boolean;
  showDay?: boolean;
  showWeekday?: boolean;
  showTime?: boolean;
  staticMessage?: (value: React.ReactNode) => React.ReactNode;
  value: DateTime | string;
}

export function FormattedDateTime({
  dateStyle = 'short',
  relative = false,
  relativeStyle,
  relativeMessage = defaultRelativeMessage,
  relativeThreshold = Duration.fromObject({ year: -1 }),
  showDate = true,
  showYear = true,
  showMonth = true,
  showDay = true,
  showWeekday = false,
  showTime = false,
  staticMessage = defaultStaticMessage,
  value
}: FormattedDateTimeProps) {
  const { locale } = useIntl();

  const relativeTimeFormatter = useMemo(
    () =>
      new Intl.RelativeTimeFormat(locale, {
        style: relativeStyle || (dateStyle !== 'numeric' ? dateStyle : 'long')
      }),
    [dateStyle, locale, relativeStyle]
  );

  const date = typeof value === 'string' ? DateTime.fromISO(value) : value;
  if (date && (!DateTime.isDateTime(date) || !date.isValid)) {
    throw new Error('invalid date');
  }

  if (relative) {
    const timeAgo = date.diffNow('seconds');

    if (relativeThreshold === null || timeAgo > relativeThreshold) {
      let durationDisplay: React.ReactNode;

      if (timeAgo.seconds > -10 && timeAgo.seconds <= 0) {
        durationDisplay = <FormattedMessage id="ui:date-time.just-now" defaultMessage="just now" />;
      } else {
        const significantUnit = getDurationSignificantUnit(timeAgo);

        durationDisplay = (
          <time dateTime={date.toUTC().toISO()!}>
            {relativeTimeFormatter.format(significantUnit.value, significantUnit.unit)}
          </time>
        );
      }

      return relativeMessage(durationDisplay);
    }
  }

  const formattedDate = (
    <time dateTime={date.toUTC().toISO()!} suppressHydrationWarning>
      {formatDateTime(locale, date, {
        dateStyle,
        showDate,
        showYear,
        showMonth,
        showDay,
        showWeekday,
        showTime
      })}
    </time>
  );

  return staticMessage(formattedDate);
}

function defaultRelativeMessage(timeAgo: React.ReactNode) {
  return timeAgo;
}

function defaultStaticMessage(msg: React.ReactNode) {
  return msg;
}
